import { useMemo, useState } from 'react'
import { GitBranch, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { COMPARISON_OPERATORS, formulaToHuman, toStringLiteral } from '@/lib/formula'

/** A field (or library placeholder) selectable as a formula operand. */
export interface ConditionOperand {
  id: string
  label: string
}

// ─── Editor state model ───────────────────────────────────────────────────────
// A structured IF is edited as a tree and serialized to the formula string only
// when the admin clicks Insert — so a half-built condition never pollutes the
// stored formula (which is what made the raw IF ( , , ) scaffold unusable).

export type OperandState =
  | { kind: 'empty' }
  | { kind: 'field'; id: string }
  | { kind: 'number'; v: string }
  | { kind: 'text'; v: string }
  | { kind: 'expr'; v: string }
  | { kind: 'newref'; v: string }

interface ConditionRow {
  left: OperandState
  op: string
  right: OperandState
  /** How this row combines with the previous one (ignored on the first row). */
  join: 'AND' | 'OR'
}

type BranchState =
  | { kind: 'value'; operand: OperandState }
  | { kind: 'if'; node: IfNode }

interface IfNode {
  rows: ConditionRow[]
  then: BranchState
  els: BranchState
}

export const emptyOperand = (): OperandState => ({ kind: 'empty' })
const emptyRow = (): ConditionRow => ({
  left: emptyOperand(),
  op: '>',
  right: emptyOperand(),
  join: 'AND',
})
const emptyNode = (): IfNode => ({
  rows: [emptyRow()],
  then: { kind: 'value', operand: emptyOperand() },
  els: { kind: 'value', operand: emptyOperand() },
})

// ─── Serialization ────────────────────────────────────────────────────────────

function slugRef(name: string): string {
  return name.trim().replace(/[{}]/g, '').replace(/\s+/g, '_')
}

export function serializeOperand(o: OperandState): string | null {
  switch (o.kind) {
    case 'empty':
      return null
    case 'field':
      return o.id ? `{${o.id}}` : null
    case 'number': {
      const t = o.v.trim()
      return t !== '' && Number.isFinite(Number(t)) ? t : null
    }
    case 'text':
      return toStringLiteral(o.v)
    case 'expr': {
      const t = o.v.trim()
      return t ? `(${t})` : null
    }
    case 'newref': {
      const c = slugRef(o.v)
      return c ? `{${c}}` : null
    }
  }
}

function serializeBranch(b: BranchState): string | null {
  return b.kind === 'value' ? serializeOperand(b.operand) : serializeNode(b.node)
}

function serializeNode(n: IfNode): string | null {
  if (n.rows.length === 0) return null
  const parts: string[] = []
  for (const r of n.rows) {
    const l = serializeOperand(r.left)
    const rt = serializeOperand(r.right)
    if (!l || !rt) return null
    parts.push(`${l} ${r.op} ${rt}`)
  }
  const joins = new Set(n.rows.slice(1).map(r => r.join))
  let cond: string
  if (parts.length === 1) {
    cond = parts[0]
  } else if (joins.size === 1) {
    // Uniform joins read best as the variadic form: ALL(c1, c2, …) / ANY(c1, c2, …).
    cond = `${joins.has('AND') ? 'ALL' : 'ANY'}(${parts.join(', ')})`
  } else {
    // Mixed AND/OR: explicit parens per pair so it reads top-to-bottom, not by precedence.
    cond = parts.reduce((acc, c, i) => (i === 0 ? c : `(${acc} ${n.rows[i].join} ${c})`))
  }
  const then = serializeBranch(n.then)
  const els = serializeBranch(n.els)
  if (!then || !els) return null
  return `IF(${cond}, ${then}, ${els})`
}

// ─── Operand input ────────────────────────────────────────────────────────────

export function OperandInput({
  value,
  onChange,
  operands,
  allowCustomRefs,
  placeholder,
}: {
  value: OperandState
  onChange: (o: OperandState) => void
  operands: ConditionOperand[]
  allowCustomRefs?: boolean
  placeholder?: string
}) {
  const selectValue =
    value.kind === 'field' ? `field:${value.id}` : value.kind === 'empty' ? '' : `__${value.kind}`

  return (
    <div className='flex min-w-0 flex-1 flex-col gap-1'>
      <Select
        value={selectValue || undefined}
        onValueChange={v => {
          if (v.startsWith('field:')) onChange({ kind: 'field', id: v.slice(6) })
          else if (v === '__number') onChange({ kind: 'number', v: '' })
          else if (v === '__text') onChange({ kind: 'text', v: '' })
          else if (v === '__expr') onChange({ kind: 'expr', v: '' })
          else if (v === '__newref') onChange({ kind: 'newref', v: '' })
        }}
      >
        <SelectTrigger className='h-8 text-xs'>
          <SelectValue placeholder={placeholder ?? 'Choose…'} />
        </SelectTrigger>
        <SelectContent>
          {operands.map(o => (
            <SelectItem key={o.id} value={`field:${o.id}`}>
              {o.label}
            </SelectItem>
          ))}
          {allowCustomRefs && <SelectItem value='__newref'>New placeholder…</SelectItem>}
          <SelectItem value='__number'>Number…</SelectItem>
          <SelectItem value='__text'>Text…</SelectItem>
          <SelectItem value='__expr'>Expression…</SelectItem>
        </SelectContent>
      </Select>

      {(value.kind === 'number' ||
        value.kind === 'text' ||
        value.kind === 'expr' ||
        value.kind === 'newref') && (
        <Input
          value={value.v}
          className='h-7 text-xs'
          placeholder={
            value.kind === 'number'
              ? 'e.g. 100'
              : value.kind === 'text'
                ? 'e.g. Pass'
                : value.kind === 'newref'
                  ? 'e.g. weight'
                  : 'e.g. {a} / {b} * 100'
          }
          onChange={e => onChange({ ...value, v: e.target.value })}
        />
      )}

      {/* Expression helper chips — click to append a field token, no ID typing. */}
      {value.kind === 'expr' && operands.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {operands.map(o => (
            <button
              key={o.id}
              type='button'
              className='rounded-full border px-1.5 py-0.5 text-[10px] transition-colors hover:border-primary hover:bg-primary/5'
              title={`Insert {${o.id}}`}
              onClick={() =>
                onChange({ kind: 'expr', v: `${value.v}${value.v ? ' ' : ''}{${o.id}}` })
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Branch (then / else) editor ─────────────────────────────────────────────

function BranchEditor({
  label,
  branch,
  onChange,
  operands,
  allowCustomRefs,
  depth,
}: {
  label: string
  branch: BranchState
  onChange: (b: BranchState) => void
  operands: ConditionOperand[]
  allowCustomRefs?: boolean
  depth: number
}) {
  return (
    <div className='space-y-1'>
      <div className='flex items-center justify-between'>
        <span className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
          {label}
        </span>
        <button
          type='button'
          className='text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
          onClick={() =>
            onChange(
              branch.kind === 'value'
                ? { kind: 'if', node: emptyNode() }
                : { kind: 'value', operand: emptyOperand() },
            )
          }
        >
          {branch.kind === 'value' ? '+ nested condition' : 'use a value instead'}
        </button>
      </div>
      {branch.kind === 'value' ? (
        <OperandInput
          value={branch.operand}
          onChange={operand => onChange({ kind: 'value', operand })}
          operands={operands}
          allowCustomRefs={allowCustomRefs}
          placeholder='Result…'
        />
      ) : (
        <div className='rounded-md border-l-2 border-primary/40 bg-muted/20 py-1.5 pl-2 pr-1'>
          <IfNodeEditor
            node={branch.node}
            onChange={node => onChange({ kind: 'if', node })}
            operands={operands}
            allowCustomRefs={allowCustomRefs}
            depth={depth + 1}
          />
        </div>
      )}
    </div>
  )
}

// ─── Recursive IF editor ─────────────────────────────────────────────────────

function IfNodeEditor({
  node,
  onChange,
  operands,
  allowCustomRefs,
  depth,
}: {
  node: IfNode
  onChange: (n: IfNode) => void
  operands: ConditionOperand[]
  allowCustomRefs?: boolean
  depth: number
}) {
  const patchRow = (idx: number, patch: Partial<ConditionRow>) =>
    onChange({
      ...node,
      rows: node.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    })

  return (
    <div className='space-y-2'>
      <div className='space-y-1.5'>
        <span className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
          If
        </span>
        {node.rows.map((row, i) => (
          <div key={i} className='space-y-1'>
            {i > 0 && (
              <Select
                value={row.join}
                onValueChange={v => patchRow(i, { join: v as 'AND' | 'OR' })}
              >
                <SelectTrigger className='h-6 w-20 text-[11px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='AND'>AND</SelectItem>
                  <SelectItem value='OR'>OR</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className='flex items-start gap-1'>
              <OperandInput
                value={row.left}
                onChange={left => patchRow(i, { left })}
                operands={operands}
                allowCustomRefs={allowCustomRefs}
                placeholder='Field…'
              />
              <Select value={row.op} onValueChange={op => patchRow(i, { op })}>
                <SelectTrigger className='h-8 w-14 shrink-0 px-2 text-xs'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPARISON_OPERATORS.map(o => (
                    <SelectItem key={o.token} value={o.token}>
                      {o.symbol}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <OperandInput
                value={row.right}
                onChange={right => patchRow(i, { right })}
                operands={operands}
                allowCustomRefs={allowCustomRefs}
                placeholder='Value…'
              />
              {node.rows.length > 1 && (
                <Button
                  type='button'
                  size='icon'
                  variant='ghost'
                  className='h-8 w-6 shrink-0 text-muted-foreground'
                  title='Remove condition'
                  onClick={() =>
                    onChange({ ...node, rows: node.rows.filter((_, j) => j !== i) })
                  }
                >
                  <X className='h-3.5 w-3.5' />
                </Button>
              )}
            </div>
          </div>
        ))}
        <Button
          type='button'
          size='sm'
          variant='ghost'
          className='h-6 px-1.5 text-[11px] text-muted-foreground'
          onClick={() => onChange({ ...node, rows: [...node.rows, emptyRow()] })}
        >
          <Plus className='mr-1 h-3 w-3' /> Add condition
        </Button>
      </div>

      <BranchEditor
        label='Then'
        branch={node.then}
        onChange={then => onChange({ ...node, then })}
        operands={operands}
        allowCustomRefs={allowCustomRefs}
        depth={depth}
      />
      <BranchEditor
        label='Else'
        branch={node.els}
        onChange={els => onChange({ ...node, els })}
        operands={operands}
        allowCustomRefs={allowCustomRefs}
        depth={depth}
      />
    </div>
  )
}

// ─── Public component ────────────────────────────────────────────────────────

interface Props {
  /** Fields (or library placeholders) offered in the operand pickers. */
  operands: ConditionOperand[]
  /** Library mode: allow typing a brand-new placeholder name in any slot. */
  allowCustomRefs?: boolean
  /** Called with the complete, valid IF(...) expression. */
  onInsert: (formula: string) => void
  onCancel: () => void
  className?: string
}

/**
 * Structured IF/THEN/ELSE editor. Builds the condition as a tree (with AND/OR
 * rows and arbitrary nesting in either branch) and only emits the formula
 * once every slot is filled — replacing the raw `IF ( , , )` token scaffold.
 */
export function ConditionBuilder({
  operands,
  allowCustomRefs,
  onInsert,
  onCancel,
  className,
}: Props) {
  const [node, setNode] = useState<IfNode>(emptyNode)

  const serialized = useMemo(() => serializeNode(node), [node])

  const preview = useMemo(() => {
    if (!serialized) return null
    const labels: Record<string, string> = {}
    for (const o of operands) labels[o.id] = o.label
    // New placeholders aren't in the operand list — label them with their own name.
    for (const m of serialized.matchAll(/\{([^}]+)\}/g)) {
      if (!(m[1] in labels)) labels[m[1]] = m[1]
    }
    return formulaToHuman(serialized, labels)
  }, [serialized, operands])

  return (
    <div className={cn('space-y-2 rounded-md border bg-card p-2.5', className)}>
      <div className='flex items-center gap-1.5'>
        <GitBranch className='h-3.5 w-3.5 text-muted-foreground' />
        <span className='text-xs font-semibold'>Build IF condition</span>
      </div>

      <IfNodeEditor
        node={node}
        onChange={setNode}
        operands={operands}
        allowCustomRefs={allowCustomRefs}
        depth={0}
      />

      {preview ? (
        <div className='rounded bg-muted/40 px-2 py-1.5'>
          <p className='text-[10px] uppercase tracking-wide text-muted-foreground'>Preview</p>
          <p className='text-xs font-medium break-words'>{preview}</p>
        </div>
      ) : (
        <p className='text-[11px] text-muted-foreground'>
          Fill every slot to enable Insert.
        </p>
      )}

      <div className='flex items-center justify-end gap-2'>
        <Button type='button' size='sm' variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={!serialized}
          onClick={() => serialized && onInsert(serialized)}
        >
          Insert condition
        </Button>
      </div>
    </div>
  )
}
